'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslation } from '../lib/i18n';
import { useFormatLocale } from '../lib/useNationalEnvironment';
import { useAuth } from '../lib/auth';
import { apiFetch } from '../lib/api';
import { LocalizedDateInput, LocalizedTimeInput } from './LocalizedDateInput';
import AccountContactPicker from './AccountContactPicker';
import LeadPicker from './LeadPicker';
import ConfirmModal from './ConfirmModal';
import AuditInfoLine from './AuditInfoLine';
import PhoneInput from './PhoneInput';
import {
  type EventType,
  type EventStatus,
  type CalendarEvent,
  type UserOption,
  type TeamOption,
  toLocalDateInput,
  formatDateTime,
} from './EventFormModal';
import type { UseEventFormReturn } from './useEventForm';

type RecurrenceType = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export type EventFormFieldsProps = {
  form: UseEventFormReturn;
  allowTypeChange: boolean;
  userOptions: UserOption[];
  teamOptions: TeamOption[];
  /** Called when a user link is clicked (e.g. to close modal/inline) */
  onLinkClick?: () => void;
};

export default function EventFormFields({ form, allowTypeChange, userOptions, teamOptions, onLinkClick }: EventFormFieldsProps) {
  const { t, locale } = useTranslation();
  const fmtLocale = useFormatLocale();
  const { userId } = useAuth();
  const [privateConfirmOpen, setPrivateConfirmOpen] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  useEffect(() => {
    apiFetch<{ connected: boolean }>('/api/user/google/status', { silent: true })
      .then(s => setGoogleConnected(!!s?.connected))
      .catch(() => setGoogleConnected(false));
  }, []);

  const {
    editingEvent, formType, setFormType, formTitle, setFormTitle,
    formDescription, setFormDescription, formAllDay, setFormAllDay,
    formStatus, setFormStatus, formPriority, setFormPriority,
    formLocation, setFormLocation, formParticipants, setFormParticipants,
    formPhone, setFormPhone, formCallResult, setFormCallResult,
    formImChannel, setFormImChannel,
    formDuration, setFormDuration, formRecurrence, setFormRecurrence,
    formRecurrenceInterval, setFormRecurrenceInterval,
    formRecurrenceEndDate, setFormRecurrenceEndDate, formRecurrenceEndTime, setFormRecurrenceEndTime,
    formDueDateDate, setFormDueDateDate, formDueDateTime, setFormDueDateTime,
    formColor, setFormColor, formChecklist, newChecklistText, setNewChecklistText,
    formReminders, formAssigneeIds, setFormAssigneeIds, formWatcherIds, setFormWatcherIds,
    formTeamId, setFormTeamId, formPrivate, setFormPrivate, formGoogleSync, setFormGoogleSync,
    formAccountId, setFormAccountId, formAccountName, setFormAccountName,
    formAccountPohodaId, setFormAccountPohodaId,
    formContactId, setFormContactId, formContactName, setFormContactName,
    formLeadId, setFormLeadId, formLeadName, setFormLeadName,
    formRefType, formRefId,
    formStartDate, setFormStartDate, formStartTime, setFormStartTime,
    formEndDate, setFormEndDate, formEndTime, setFormEndTime,
    formStart, formEnd, checklistProgress,
    addChecklistItem, toggleChecklistItem, removeChecklistItem,
    addReminder, updateReminder, removeReminder,
    toggleAssignee, toggleWatcher, cancelSnooze,
  } = form;

  const RECURRENCE_PRESETS = ['DAILY:1', 'WEEKLY:1', 'WEEKLY:2', 'MONTHLY:1', 'MONTHLY:3', 'MONTHLY:6', 'YEARLY:1'];
  const recurrenceSelectValue = formRecurrence === 'NONE' ? 'NONE' : `${formRecurrence}:${formRecurrenceInterval}`;
  const isRecurrencePreset = recurrenceSelectValue === 'NONE' || RECURRENCE_PRESETS.includes(recurrenceSelectValue);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left column: core fields + reminders */}
      <div className="space-y-3">
        {/* Type, status, priority in one row */}
        <div className={`grid gap-2 ${allowTypeChange ? 'grid-cols-1 sm:grid-cols-3' : (formType === 'TASK' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}`}>
          {allowTypeChange && (
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarEventType')}</label>
              <select value={formType} onChange={e => setFormType(e.target.value as EventType)}
                className="w-full rounded-rustic border border-wheat bg-cream px-2 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
                <option value="TASK">{t('calendarTask')}</option>
                <option value="MEETING">{t('calendarMeeting')}</option>
                <option value="CALL">{t('calendarCall')}</option>
                <option value="IM">{t('calendarIM')}</option>
                <option value="NOTE">{t('calendarNote')}</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-wood mb-1">{t('calendarStatus')}</label>
            <div className="flex gap-1.5">
              <select value={formStatus} onChange={(e) => setFormStatus(e.target.value as EventStatus)}
                className="flex-1 rounded-rustic border border-wheat bg-cream px-2 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
                <option value="PENDING">{t('calendarStatusPending')}</option>
                <option value="IN_PROGRESS">{t('calendarStatusInProgress')}</option>
                <option value="COMPLETED">{t('calendarStatusCompleted')}</option>
                <option value="CANCELLED">{t('calendarStatusCancelled')}</option>
              </select>
              <button
                type="button"
                onClick={() => setFormStatus(formStatus === 'COMPLETED' ? 'PENDING' : 'COMPLETED')}
                title={formStatus === 'COMPLETED' ? t('eventReopen') : t('eventMarkDone')}
                className={`px-3 rounded-rustic border text-sm font-bold ${formStatus === 'COMPLETED' ? 'bg-moss border-moss text-cream' : 'border-wheat bg-cream text-moss hover:bg-moss/10'}`}
              >
                ✓
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-wood mb-1">{t('calendarPriority')}</label>
            <select value={formPriority} onChange={(e) => setFormPriority(e.target.value)}
              className="w-full rounded-rustic border border-wheat bg-cream px-2 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
              <option value="">—</option>
              <option value="LOW">{t('calendarPriorityLow')}</option>
              <option value="MEDIUM">{t('calendarPriorityMedium')}</option>
              <option value="HIGH">{t('calendarPriorityHigh')}</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-wood mb-1">{t('calendarEventTitle')}</label>
          <input type="text" value={formTitle} onChange={(e) => setFormTitle(e.target.value)}
            className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss"
            placeholder={t('calendarEventTitle')} />
        </div>
        <div>
          <label className="block text-xs font-medium text-wood mb-1">{t('calendarDescription')}</label>
          <textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={3}
            className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
        </div>
        {/* Start date + time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-wood mb-1">{t('calendarStartDate') || t('calendarStart')}</label>
            <LocalizedDateInput value={formStartDate} onChange={v => setFormStartDate(v)} locale={locale} disabled={formAllDay}
              className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-wood mb-1">{t('calendarStartTime')}</label>
            <LocalizedTimeInput value={formStartTime} onChange={v => setFormStartTime(v)} disabled={formAllDay}
              className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50" />
          </div>
        </div>
        {/* Deadline for tasks */}
        {formType === 'TASK' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarDueDate')}</label>
              <LocalizedDateInput value={formDueDateDate} onChange={v => setFormDueDateDate(v)} locale={locale} min={formStartDate}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
              <div className="flex gap-1 mt-1">
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { setFormDueDateDate(formStartDate); setFormDueDateTime(formStartTime); }}>{t('calendarStart')}</button>
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { const d = new Date((formDueDateDate || formStartDate || toLocalDateInput(new Date())) + 'T00:00:00'); d.setDate(d.getDate() + 1); setFormDueDateDate(toLocalDateInput(d)); }}>{t('calendarPlus1Day')}</button>
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { const d = new Date((formDueDateDate || formStartDate || toLocalDateInput(new Date())) + 'T00:00:00'); d.setDate(d.getDate() + 7); setFormDueDateDate(toLocalDateInput(d)); }}>{t('calendarPlus1Week')}</button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarTime')}</label>
              <LocalizedTimeInput value={formDueDateTime} onChange={v => setFormDueDateTime(v)}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
              <div className="flex gap-1 mt-1">
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { if (formDueDateTime) { const [h,m] = formDueDateTime.split(':').map(Number); const d = new Date(); d.setHours(h||0, m||0, 0, 0); d.setMinutes(d.getMinutes() + 10); setFormDueDateTime(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`); } }}>{t('calendarPlus10min')}</button>
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { if (formDueDateTime) { const [h,m] = formDueDateTime.split(':').map(Number); const d = new Date(); d.setHours(h||0, m||0, 0, 0); d.setMinutes(d.getMinutes() + 30); setFormDueDateTime(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`); } }}>{t('calendarPlus30min')}</button>
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { if (formDueDateTime) { const [h,m] = formDueDateTime.split(':').map(Number); const d = new Date(); d.setHours(h||0, m||0, 0, 0); d.setHours(d.getHours() + 1); setFormDueDateTime(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`); } }}>{t('calendarPlus1h')}</button>
              </div>
            </div>
          </div>
        )}
        {/* End date + time — hidden for TASK */}
        {formType !== 'TASK' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarEndDate') || t('calendarEnd')}</label>
              <LocalizedDateInput value={formEndDate} onChange={v => setFormEndDate(v)} locale={locale} disabled={formAllDay}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50" />
              <div className="flex gap-1 mt-1">
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { const d = new Date((formEndDate || formStartDate || toLocalDateInput(new Date())) + 'T00:00:00'); d.setDate(d.getDate() + 1); setFormEndDate(toLocalDateInput(d)); }}>{t('calendarPlus1Day')}</button>
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { const d = new Date((formEndDate || formStartDate || toLocalDateInput(new Date())) + 'T00:00:00'); d.setDate(d.getDate() + 7); setFormEndDate(toLocalDateInput(d)); }}>{t('calendarPlus1Week')}</button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarEndTime')}</label>
              <LocalizedTimeInput value={formEndTime} onChange={v => setFormEndTime(v)} disabled={formAllDay}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50" />
              <div className="flex gap-1 mt-1">
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { if (formEndTime) { const [h,m] = formEndTime.split(':').map(Number); const d = new Date(); d.setHours(h||0, m||0, 0, 0); d.setMinutes(d.getMinutes() + 10); setFormEndTime(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`); } }}>{t('calendarPlus10min')}</button>
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { if (formEndTime) { const [h,m] = formEndTime.split(':').map(Number); const d = new Date(); d.setHours(h||0, m||0, 0, 0); d.setMinutes(d.getMinutes() + 30); setFormEndTime(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`); } }}>{t('calendarPlus30min')}</button>
                <button type="button" className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                  onClick={() => { if (formEndTime) { const [h,m] = formEndTime.split(':').map(Number); const d = new Date(); d.setHours(h||0, m||0, 0, 0); d.setHours(d.getHours() + 1); setFormEndTime(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`); } }}>{t('calendarPlus1h')}</button>
              </div>
            </div>
          </div>
        )}
        {formType !== 'TASK' && !formAllDay && formEnd < formStart && (
          <div className="text-xs text-rust-dark">{t('calendarEndAfterStart')}</div>
        )}
        <label className="flex items-center gap-2 text-sm text-wood">
          <input type="checkbox" checked={formAllDay} onChange={(e) => setFormAllDay(e.target.checked)} className="rounded border-wheat" />
          {t('calendarAllDay')}
        </label>
        {/* Recurrence — moved to end of left column */}
        <div className="rounded-rustic border border-wheat bg-cream/30 p-3 space-y-2">
          <div className="text-xs font-medium text-wood">↻ {t('calendarRecurrence')}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-wood mb-1">{t('calendarRecurrence')}</label>
              <select value={recurrenceSelectValue}
                onChange={e => {
                  const v = e.target.value;
                  if (v === 'NONE') { setFormRecurrence('NONE'); return; }
                  const [freq, intv] = v.split(':');
                  setFormRecurrence(freq as RecurrenceType);
                  setFormRecurrenceInterval(parseInt(intv, 10) || 1);
                }}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
                <option value="NONE">—</option>
                <option value="DAILY:1">{t('calendarRecDaily')}</option>
                <option value="WEEKLY:1">{t('calendarRecWeekly')}</option>
                <option value="WEEKLY:2">{t('calendarRecBiweekly')}</option>
                <option value="MONTHLY:1">{t('calendarRecMonthly')}</option>
                <option value="MONTHLY:3">{t('calendarRecQuarterly')}</option>
                <option value="MONTHLY:6">{t('calendarRecSemiannual')}</option>
                <option value="YEARLY:1">{t('calendarRecYearly')}</option>
                {!isRecurrencePreset && <option value={recurrenceSelectValue}>{t('calendarRecCustom')}</option>}
              </select>
            </div>
            {formRecurrence !== 'NONE' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-wood mb-1">{t('calendarRecInterval')}</label>
                  <input type="text" inputMode="numeric" min={1} max={999} value={formRecurrenceInterval}
                    onChange={e => setFormRecurrenceInterval(Math.max(1, Math.min(999, parseInt(e.target.value || '1', 10))))}
                    className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
                </div>
                <div>
                  <label className="block text-xs text-wood mb-1">{t('calendarRecEnd')}</label>
                  <LocalizedDateInput value={formRecurrenceEndDate} onChange={v => setFormRecurrenceEndDate(v)} locale={locale}
                    className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
                </div>
                <div>
                  <label className="block text-xs text-wood mb-1">{t('calendarStartTime')}</label>
                  <LocalizedTimeInput value={formRecurrenceEndTime} onChange={v => setFormRecurrenceEndTime(v)}
                    className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Middle column: type-specific, checklist, vazby */}
      <div className="space-y-3">
        {/* Type-specific fields */}
        {formType === 'MEETING' && (
          <>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarLocation')}</label>
              <input type="text" value={formLocation} onChange={(e) => setFormLocation(e.target.value)}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
            </div>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarParticipants')}</label>
              <input type="text" value={formParticipants} onChange={(e) => setFormParticipants(e.target.value)}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss"
                placeholder="email@example.com, ..." />
            </div>
          </>
        )}
        {formType === 'CALL' && (
          <>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarPhone')}</label>
              <PhoneInput value={formPhone} onChange={setFormPhone} showLabel={false}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
            </div>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarCallResult')}</label>
              <select value={formCallResult} onChange={(e) => setFormCallResult(e.target.value)}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
                <option value="">{t('calendarCallResultNone')}</option>
                <option value="CONNECTED">{t('calendarCallResultConnected')}</option>
                <option value="NOT_CONNECTED">{t('calendarCallResultNotConnected')}</option>
                <option value="BUSY">{t('calendarCallResultBusy')}</option>
                <option value="AGREED">{t('calendarCallResultAgreed')}</option>
                <option value="DECLINED">{t('calendarCallResultDeclined')}</option>
                <option value="CALL_BACK">{t('calendarCallResultCallBack')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarDuration')}</label>
              <input type="text" inputMode="numeric" value={formDuration} onChange={(e) => setFormDuration(e.target.value)}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
            </div>
          </>
        )}
        {formType === 'IM' && (
          <>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarImChannel')}</label>
              <select value={formImChannel} onChange={(e) => setFormImChannel(e.target.value)}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
                <option value="">{t('calendarImChannelNone')}</option>
                <option value="WHATSAPP">{t('calendarImChannelWhatsapp')}</option>
                <option value="FACEBOOK">{t('calendarImChannelFacebook')}</option>
                <option value="INSTAGRAM">{t('calendarImChannelInstagram')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-wood mb-1">{t('calendarPhone')}</label>
              <PhoneInput value={formPhone} onChange={setFormPhone} showLabel={false}
                className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
            </div>
          </>
        )}
        {/* Checklist for tasks */}
        {formType === 'TASK' && (
          <div>
            <label className="block text-xs font-medium text-wood mb-1">{t('calendarChecklist')} {formChecklist.length > 0 && `(${checklistProgress}%)`}</label>
            {formChecklist.length > 0 && (
              <div className="h-1.5 bg-wheat rounded-full overflow-hidden mb-2">
                <div className="h-full bg-moss transition-all" style={{ width: `${checklistProgress}%` }}></div>
              </div>
            )}
            {formChecklist.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1 group">
                <button
                  type="button"
                  onClick={() => toggleChecklistItem(idx)}
                  aria-pressed={item.done}
                  aria-label={item.done ? t('calendarMarkUndone') : t('calendarMarkDone')}
                  title={item.done ? t('calendarMarkUndone') : t('calendarMarkDone')}
                  className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                    item.done
                      ? 'bg-moss border-moss text-cream'
                      : 'bg-cream border-wheat text-transparent hover:border-moss hover:bg-moss/10'
                  }`}
                >
                  <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8.5 L6.5 12 L13 4" />
                  </svg>
                </button>
                <span className={`text-sm flex-1 ${item.done ? 'line-through text-wood/70' : 'text-bark'}`}>{item.text}</span>
                {item.done && item.doneAt && (
                  <span className="text-xs text-wood/70 whitespace-nowrap" title={item.doneAt}>
                    {formatDateTime(item.doneAt, fmtLocale)}
                  </span>
                )}
                <button onClick={() => removeChecklistItem(idx)} aria-label={t('removeChecklistItem')} title={t('removeChecklistItem')} className="text-xs text-rust hover:text-rust-dark opacity-0 group-hover:opacity-100 transition-opacity">×</button>
              </div>
            ))}
            <div className="flex gap-2">
              <input type="text" value={newChecklistText} onChange={e => setNewChecklistText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
                placeholder={t('calendarAddChecklist')}
                className="flex-1 rounded-rustic border border-wheat bg-cream px-3 py-1.5 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
              <button onClick={addChecklistItem} className="btn-secondary px-3 py-1.5">+</button>
            </div>
          </div>
        )}
        {/* Vazby: Account & Contact, Lead */}
        <div className="border-t border-wheat pt-3">
          <AccountContactPicker
            accountId={formAccountId}
            accountPohodaId={formAccountPohodaId}
            accountName={formAccountName}
            contactId={formContactId}
            contactName={formContactName}
            onChange={next => {
              setFormAccountId(next.accountId);
              setFormAccountName(next.accountName);
              if (next.accountPohodaId !== undefined) setFormAccountPohodaId(next.accountPohodaId);
              setFormContactId(next.contactId);
              setFormContactName(next.contactName);
            }}
          />
          <LeadPicker
            leadId={formLeadId}
            leadName={formLeadName}
            onChange={next => {
              setFormLeadId(next.leadId);
              setFormLeadName(next.leadName);
            }}
          />
        </div>

        {/* Multiple Reminders — after lead */}
        <div>
          <label className="block text-xs font-medium text-wood mb-1">{t('calendarReminders')}</label>
          {formReminders.map((r, idx) => {
            const refTime = (r.refPoint === 'DEADLINE' && formDueDateDate)
              ? new Date(`${formDueDateDate}T${formDueDateTime || '00:00'}`)
              : new Date(formStart);
            const reminderTime = !isNaN(refTime.getTime()) && r.minutes
              ? new Date(refTime.getTime() - Number(String(r.minutes).replace(',', '.')) * 60 * 1000)
              : null;
            return (
            <div key={idx} className="mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                {r.refPoint === 'ABSOLUTE' ? (
                  <>
                    <LocalizedDateInput value={(r.absoluteTime || '').split('T')[0] || ''}
                      onChange={v => {
                        const time = (r.absoluteTime || '').split('T')[1] || '09:00';
                        updateReminder(idx, 'absoluteTime', v ? `${v}T${time}` : '');
                      }}
                      locale={locale}
                      className="w-32 rounded-rustic border border-wheat bg-cream px-2 py-1.5 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
                    <LocalizedTimeInput value={(r.absoluteTime || '').split('T')[1]?.substring(0, 5) || ''}
                      onChange={v => {
                        const date = (r.absoluteTime || '').split('T')[0] || toLocalDateInput(new Date());
                        updateReminder(idx, 'absoluteTime', `${date}T${v}`);
                      }}
                      className="w-20 rounded-rustic border border-wheat bg-cream px-2 py-1.5 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
                  </>
                ) : (
                  <>
                    <input type="text" inputMode="numeric" value={r.minutes} onChange={e => updateReminder(idx, 'minutes', e.target.value)} min={1}
                      className="w-20 rounded-rustic border border-wheat bg-cream px-2 py-1.5 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss" />
                    <span className="text-xs text-wood">{t('calendarMinutes')}</span>
                  </>
                )}
                <select value={r.refPoint || 'START'} onChange={e => updateReminder(idx, 'refPoint', e.target.value)}
                  className="rounded-rustic border border-wheat bg-cream px-2 py-1.5 text-xs text-bark focus:outline-none focus:ring-1 focus:ring-moss">
                  <option value="START">{t('calendarReminderRefStart')}</option>
                  {formType === 'TASK' && (
                    <option value="DEADLINE" disabled={!formDueDateDate}>{t('calendarReminderRefDeadline')}{!formDueDateDate ? ' (' + t('calendarNoDeadline') + ')' : ''}</option>
                  )}
                  <option value="ABSOLUTE">{t('calendarReminderRefAbsolute')}</option>
                </select>
                <select value={r.type} onChange={e => updateReminder(idx, 'type', e.target.value)}
                  className="rounded-rustic border border-wheat bg-cream px-2 py-1.5 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
                  <option value="APP">{t('calendarReminderApp')}</option>
                  <option value="EMAIL">{t('calendarReminderEmail')}</option>
                </select>
                <button onClick={() => removeReminder(idx)} aria-label={t('removeReminder')} title={t('removeReminder')} className="text-xs text-rust hover:text-rust-dark">×</button>
              </div>
              {r.refPoint === 'ABSOLUTE' && r.absoluteTime ? (
                <div className="text-xs text-wood/60 pl-1 mt-0.5">→ {formatDateTime(r.absoluteTime, fmtLocale)}</div>
              ) : reminderTime ? (
                <div className="text-xs text-wood/60 pl-1 mt-0.5">→ {formatDateTime(reminderTime.toISOString(), fmtLocale)}</div>
              ) : null}
            </div>
            );
          })}
          <button onClick={addReminder} className="text-xs text-moss-dark hover:text-moss font-medium">+ {t('calendarAddReminder')}</button>
          {editingEvent?.snoozedUntil && new Date(editingEvent.snoozedUntil) > new Date() && (
            <div className="mt-2 flex items-center gap-2 rounded-rustic border border-ochre/30 bg-ochre/20 px-3 py-2 text-xs text-ochre">
              <span>{t('snoozedUntil')}: {formatDateTime(editingEvent.snoozedUntil, fmtLocale)}</span>
              <button onClick={cancelSnooze} className="text-ochre hover:text-rust-dark font-medium underline">{t('cancelSnooze')}</button>
            </div>
          )}
        </div>
      </div>

      {/* Right column: private, assignees, team, watchers */}
      <div className="space-y-3">
        {/* Private — only the owner may toggle it (non-owner flipping it on would lock everyone out) */}
        {(!editingEvent || editingEvent.userId === userId) && (
        <div>
          <button
            type="button"
            onClick={() => {
              if (formPrivate) {
                // Turning OFF private — no confirmation needed, nothing is destroyed.
                setFormPrivate(false);
              } else {
                // Turning ON private — check if there's anything to destroy.
                const otherAssignees = formAssigneeIds.filter(uid => uid !== userId);
                const hasWatchers = formWatcherIds.length > 0;
                const hasTeam = formTeamId != null;
                if (otherAssignees.length > 0 || hasWatchers || hasTeam) {
                  setPrivateConfirmOpen(true);
                } else {
                  // Nothing to lose — switch directly.
                  setFormPrivate(true);
                }
              }
            }}
            aria-pressed={formPrivate}
            className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-rustic border text-sm font-medium transition ${
              formPrivate
                ? 'bg-moss border-moss text-cream'
                : 'bg-cream border-wheat text-bark hover:border-moss hover:bg-moss/10'
            }`}
          >
            <span className="text-base leading-none">{formPrivate ? '🔒' : '🔓'}</span>
            {formPrivate ? t('calendarPrivateDisable') : t('calendarPrivateEnable')}
          </button>
          {formPrivate && (
            <p className="mt-1 text-xs text-wood/70 flex items-start gap-1.5">
              <span className="flex-shrink-0">🔒</span>
              <span>{t('calendarPrivateHint')}</span>
            </p>
          )}
        </div>
        )}
        {/* Google sync */}
        {googleConnected && (
          <div>
            <label className="inline-flex items-center gap-2 text-sm text-bark cursor-pointer">
              <input
                type="checkbox"
                checked={formGoogleSync}
                onChange={(e) => setFormGoogleSync(e.target.checked)}
                className="w-4 h-4"
              />
              {t('calendarGoogleSync')}
            </label>
            <p className="mt-1 text-xs text-wood/70">
              {t('calendarGoogleSyncHint')}
              {editingEvent?.googleEventId && (
                <span className="ml-1.5 px-2 py-0.5 rounded-full bg-moss/15 text-moss-dark border border-moss/30 font-medium" style={editingEvent.googleCalendarColor ? { color: editingEvent.googleCalendarColor, borderColor: editingEvent.googleCalendarColor + '60' } : undefined}>
                  G{editingEvent.googleCalendarName ? `·${editingEvent.googleCalendarName}` : ` ${t('calendarSyncedGoogle')}`}
                </span>
              )}
            </p>
          </div>
        )}
        {/* Assignees */}
        <div>
          <label className="block text-xs font-medium text-wood mb-1">{t('calendarAssignees')} <span className="text-rust">*</span></label>
          <div className="rounded-rustic border border-wheat bg-cream p-2 space-y-1">
            {formAssigneeIds.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {formAssigneeIds.map(uid => {
                  const u = (userOptions || []).find(o => o.id === uid);
                  return (
                    <span key={uid} className="inline-flex items-center gap-1 bg-moss/10 text-bark text-xs rounded-full px-2 py-1">
                      <Link href={`/users/${uid}`} className="hover:text-moss hover:underline cursor-pointer" onClick={onLinkClick}>{u?.name || `#${uid}`}</Link>
                      {!formPrivate && (
                        <button type="button" onClick={() => toggleAssignee(uid)} aria-label={t('removeAssignee')} title={t('removeAssignee')} className="text-wood hover:text-rust transition">×</button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
            {formAssigneeIds.length === 0 && (
              <span className="text-xs text-rust">{t('calendarNoAssignees')}</span>
            )}
            {!formPrivate && (
              <details className="mt-1">
                <summary className="text-xs text-moss hover:text-moss-dark cursor-pointer select-none">+ {t('calendarAddAssignee')}</summary>
                <div className="mt-1 max-h-32 overflow-y-auto space-y-0.5">
                  {(userOptions || []).filter(u => !formAssigneeIds.includes(u.id)).map(u => (
                    <button key={u.id} type="button" onClick={() => toggleAssignee(u.id)}
                      className="block w-full text-left text-sm text-bark hover:bg-linen px-2 py-1 rounded transition">
                      {u.name}
                    </button>
                  ))}
                  {(!userOptions || userOptions.filter(u => !formAssigneeIds.includes(u.id)).length === 0) && (
                    <span className="text-xs text-wood px-2">{t('calendarNoUsers')}</span>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
        {/* Team */}
        {!formPrivate && (
          <div>
            <label className="block text-xs font-medium text-wood mb-1">{t('calendarTeam')}</label>
            <select value={formTeamId ?? ''} onChange={e => setFormTeamId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss">
              <option value="">—</option>
              {(teamOptions || []).map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
        )}
        {/* Watchers */}
        {!formPrivate && (
          <div>
            <label className="block text-xs font-medium text-wood mb-1">{t('calendarWatchers')}</label>
            <div className="rounded-rustic border border-wheat bg-cream p-2 space-y-1">
              {formWatcherIds.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {formWatcherIds.map(uid => {
                    const u = (userOptions || []).find(o => o.id === uid);
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 bg-moss/10 text-bark text-xs rounded-full px-2 py-1">
                        <Link href={`/users/${uid}`} className="hover:text-moss hover:underline cursor-pointer" onClick={onLinkClick}>{u?.name || `#${uid}`}</Link>
                        <button type="button" onClick={() => toggleWatcher(uid)} aria-label={t('removeWatcher')} title={t('removeWatcher')} className="text-wood hover:text-rust transition">×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              {formWatcherIds.length === 0 && (
                <span className="text-xs text-wood">{t('calendarNoWatchers')}</span>
              )}
              <details className="mt-1">
                <summary className="text-xs text-moss hover:text-moss-dark cursor-pointer select-none">+ {t('calendarAddWatcher')}</summary>
                <div className="mt-1 max-h-32 overflow-y-auto space-y-0.5">
                  {(userOptions || []).filter(u => !formWatcherIds.includes(u.id)).map(u => (
                    <button key={u.id} type="button" onClick={() => toggleWatcher(u.id)}
                      className="block w-full text-left text-sm text-bark hover:bg-linen px-2 py-1 rounded transition">
                      {u.name}
                    </button>
                  ))}
                  {(!userOptions || userOptions.filter(u => !formWatcherIds.includes(u.id)).length === 0) && (
                    <span className="text-xs text-wood px-2">{t('calendarNoUsers')}</span>
                  )}
                </div>
              </details>
            </div>
          </div>
        )}
        {(() => {
          const refType = editingEvent?.refType ?? formRefType;
          const refId = editingEvent?.refId ?? formRefId;
          const accountId = editingEvent?.accountId ?? formAccountId;
          const accountName = editingEvent?.accountName ?? formAccountName;
          const contactId = editingEvent?.contactId ?? formContactId;
          const contactName = editingEvent?.contactName ?? formContactName;
          const leadId = editingEvent?.leadId ?? formLeadId;
          const leadName = editingEvent?.leadName ?? formLeadName;
          const links: { icon: string; href: string; label: string }[] = [];
          if (refType === 'email' && refId) {
            links.push({ icon: '✉', href: `/email?message=${encodeURIComponent(refId)}`, label: t('calendarLinkedEmail') });
          }
          if (refType === 'crm_opportunity' && refId) {
            links.push({ icon: '💼', href: `/crm/opportunities/${refId}`, label: t('calendarLinkedOpportunity') });
          }
          if (refType === 'armorik_bank_statement' && refId) {
            links.push({ icon: '🏦', href: `/armorik/bank-statements?statement=${encodeURIComponent(refId)}`, label: t('calendarLinkedBankStatement') });
          }
          if (refType === 'crm_lead' && refId) {
            links.push({ icon: '🧲', href: `/crm/leads/${refId}`, label: leadName || t('calendarLinkedLead') });
          } else if (leadId != null) {
            links.push({ icon: '🧲', href: `/crm/leads/${leadId}`, label: leadName || t('calendarLinkedLead') });
          }
          if (accountId != null) {
            links.push({ icon: '🏢', href: `/accounts/${accountId}`, label: accountName || t('calendarLinkedAccount') });
          }
          if (contactId != null) {
            links.push({ icon: '👤', href: `/contacts?contactId=${contactId}`, label: contactName || t('calendarLinkedContact') });
          }
          if (links.length === 0) return null;
          return (
            <div className="flex items-center gap-3 text-sm flex-wrap">
              {links.map((l) => (
                <span key={l.href} className="flex items-center gap-1.5">
                  <span className="text-wood">{l.icon}</span>
                  <Link href={l.href} onClick={onLinkClick} className="text-moss hover:text-moss-dark underline">
                    {l.label}
                  </Link>
                </span>
              ))}
            </div>
          );
        })()}
        {editingEvent && (
          <AuditInfoLine
            createdAt={editingEvent.createdAt}
            modifiedAt={editingEvent.modifiedAt}
            createdByName={editingEvent.createdByName}
            createdById={editingEvent.createdById}
            modifiedByName={editingEvent.modifiedByName}
            modifiedById={editingEvent.modifiedById}
          />
        )}
      </div>

      {/* Private mode confirmation dialog */}
      <ConfirmModal
        open={privateConfirmOpen}
        title={t('calendarPrivateConfirmTitle')}
        message={t('calendarPrivateConfirmMsg')}
        confirmLabel={t('calendarPrivateConfirmBtn')}
        cancelLabel={t('calendarCancel')}
        onConfirm={() => {
          setFormPrivate(true);
          setFormAssigneeIds(userId ? [userId] : []);
          setFormTeamId(null);
          setFormWatcherIds([]);
          setPrivateConfirmOpen(false);
        }}
        onCancel={() => setPrivateConfirmOpen(false)}
      >
        <ul className="text-sm text-bark space-y-1 my-3 pl-1">
          {formAssigneeIds.filter(uid => uid !== userId).length > 0 && (
            <li className="flex items-center gap-2">
              <span className="text-rust">✕</span>
              <span>
                {t('calendarPrivateRemoveAssignees').replace('{count}', String(formAssigneeIds.filter(uid => uid !== userId).length))}
              </span>
            </li>
          )}
          {formWatcherIds.length > 0 && (
            <li className="flex items-center gap-2">
              <span className="text-rust">✕</span>
              <span>
                {t('calendarPrivateRemoveWatchers').replace('{count}', String(formWatcherIds.length))}
              </span>
            </li>
          )}
          {formTeamId != null && (
            <li className="flex items-center gap-2">
              <span className="text-rust">✕</span>
              <span>
                {t('calendarPrivateRemoveTeam')}: <strong>{teamOptions?.find(t2 => t2.id === formTeamId)?.name || `#${formTeamId}`}</strong>
              </span>
            </li>
          )}
        </ul>
      </ConfirmModal>
    </div>
  );
}
